import React, { useState, useMemo } from 'react';
import { Modal, Checkbox, Alert, Divider, Row, Col, Typography, Space, Tag } from 'antd';
import { DollarOutlined, CheckCircleOutlined, WarningOutlined, GiftOutlined } from '@ant-design/icons';
import { WorkOrderLineItem } from '../../types';

const { Text, Title } = Typography;

interface WaiveDiagnosisModalProps {
  visible: boolean;
  diagnosisLineItem: WorkOrderLineItem;
  approvedRepairLineItems: WorkOrderLineItem[];
  onConfirm: (repairLineItemIds: number[], distributionMethod: 'full' | 'split') => void;
  onCancel: () => void;
  loading?: boolean;
}

const WaiveDiagnosisModal: React.FC<WaiveDiagnosisModalProps> = ({
  visible,
  diagnosisLineItem,
  approvedRepairLineItems,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [selectedRepairIds, setSelectedRepairIds] = useState<number[]>([]);

  const diagnosisAmount = diagnosisLineItem.line_total || 0;

  // Calculate totals for preview
  const totalBeforeWaiver = useMemo(() => {
    const repairsTotal = approvedRepairLineItems.reduce((sum, item) => sum + (item.line_total || 0), 0);
    return repairsTotal + diagnosisAmount;
  }, [approvedRepairLineItems, diagnosisAmount]);

  const totalAfterWaiver = useMemo(() => {
    // After waiving, diagnosis is free, repairs stay the same
    return approvedRepairLineItems.reduce((sum, item) => sum + (item.line_total || 0), 0);
  }, [approvedRepairLineItems]);

  const handleOk = () => {
    if (selectedRepairIds.length === 0) {
      return; // Button should be disabled, but extra safety
    }
    onConfirm(selectedRepairIds, 'full'); // Distribution method not used anymore
  };

  const handleCheckboxChange = (repairId: number, checked: boolean) => {
    if (checked) {
      setSelectedRepairIds(prev => [...prev, repairId]);
    } else {
      setSelectedRepairIds(prev => prev.filter(id => id !== repairId));
    }
  };

  const resetState = () => {
    setSelectedRepairIds([]);
  };

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined style={{ color: '#f59e0b' }} />
          <Text strong>Waive Diagnosis Fee</Text>
        </Space>
      }
      open={visible}
      onOk={handleOk}
      onCancel={() => {
        resetState();
        onCancel();
      }}
      afterClose={resetState}
      okText="Waive Diagnosis Fee"
      okButtonProps={{
        disabled: selectedRepairIds.length === 0,
        loading,
        danger: true,
      }}
      cancelButtonProps={{ disabled: loading }}
      width={700}
      centered
    >
      {/* Diagnosis Line Item Info */}
      <Alert
        message={<Text strong>Diagnosis to Waive</Text>}
        description={
          <div>
            <div><Text>{diagnosisLineItem.concern}</Text></div>
            <Divider style={{ margin: '8px 0' }} />
            <Row justify="space-between">
              <Text>Diagnosis Fee:</Text>
              <Text strong style={{ fontSize: '18px', color: '#ef4444' }}>
                ${diagnosisAmount.toFixed(2)}
              </Text>
            </Row>
          </div>
        }
        type="warning"
        icon={<WarningOutlined />}
        showIcon
        style={{ marginBottom: '16px' }}
      />

      <Divider />

      {/* Repair Selection */}
      <div style={{ marginBottom: '16px' }}>
        <Title level={5}>
          <CheckCircleOutlined style={{ color: '#10b981', marginRight: '8px' }} />
          Confirm Approved Repairs
        </Title>
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '12px' }}>
          Select the approved repair(s) - the diagnosis will be waived (made free)
        </Text>

        {approvedRepairLineItems.length === 0 ? (
          <Alert
            message="No Approved Repairs"
            description="There are no approved repair line items to apply the diagnosis credit to. The customer must approve at least one repair before you can waive the diagnosis fee."
            type="error"
            showIcon
          />
        ) : (
          <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px' }}>
            {approvedRepairLineItems.map(repair => (
              <div
                key={repair.id}
                style={{
                  padding: '12px',
                  marginBottom: '8px',
                  backgroundColor: selectedRepairIds.includes(repair.id!) ? '#f0fdf4' : '#f9fafb',
                  border: selectedRepairIds.includes(repair.id!) ? '2px solid #10b981' : '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
                onClick={() => handleCheckboxChange(repair.id!, !selectedRepairIds.includes(repair.id!))}
              >
                <Row align="middle">
                  <Col flex="none" style={{ marginRight: '12px' }}>
                    <Checkbox
                      checked={selectedRepairIds.includes(repair.id!)}
                      onChange={(e) => handleCheckboxChange(repair.id!, e.target.checked)}
                    />
                  </Col>
                  <Col flex="auto">
                    <Text strong>{repair.concern}</Text>
                    {repair.cause && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        {repair.cause}
                      </div>
                    )}
                  </Col>
                  <Col flex="none">
                    <Tag color="green">
                      ${(repair.line_total || 0).toFixed(2)}
                    </Tag>
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview */}
      {selectedRepairIds.length > 0 && (
        <>
          <Divider />
          <Alert
            message={<Text strong>Preview: After Waiving Diagnosis</Text>}
            description={
              <div>
                <div style={{ marginBottom: '12px' }}>
                  <Row justify="space-between" style={{ marginBottom: '8px' }}>
                    <Text strong>Diagnosis: {diagnosisLineItem.concern}</Text>
                    <div style={{ textAlign: 'right' }}>
                      <Text delete type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                        ${diagnosisAmount.toFixed(2)}
                      </Text>
                      <Text strong style={{ color: '#10b981', fontSize: '16px' }}>
                        $0.00 FREE
                      </Text>
                    </div>
                  </Row>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                <div style={{ marginBottom: '12px' }}>
                  <Text strong>Approved Repairs:</Text>
                  {selectedRepairIds.map(id => {
                    const repair = approvedRepairLineItems.find(r => r.id === id);
                    if (!repair) return null;
                    return (
                      <Row key={id} justify="space-between" style={{ marginTop: '8px' }}>
                        <Col>
                          <Text style={{ fontSize: '13px' }}>{repair.concern}</Text>
                        </Col>
                        <Col>
                          <Text strong style={{ fontSize: '14px' }}>
                            ${(repair.line_total || 0).toFixed(2)}
                          </Text>
                        </Col>
                      </Row>
                    );
                  })}
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <Row justify="space-between" style={{
                  backgroundColor: '#fef3c7',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '2px solid #f59e0b',
                  marginBottom: '12px'
                }}>
                  <Text strong style={{ fontSize: '16px' }}>Original Total:</Text>
                  <Text delete type="secondary" strong style={{ fontSize: '18px' }}>
                    ${totalBeforeWaiver.toFixed(2)}
                  </Text>
                </Row>

                <Row justify="space-between" style={{
                  backgroundColor: '#f0fdf4',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '2px solid #10b981'
                }}>
                  <Text strong style={{ fontSize: '16px' }}>New Work Order Total:</Text>
                  <Text strong style={{ fontSize: '20px', color: '#10b981' }}>
                    ${totalAfterWaiver.toFixed(2)}
                  </Text>
                </Row>

                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <Text type="success" strong style={{ fontSize: '14px' }}>
                    🎉 Customer saves ${diagnosisAmount.toFixed(2)} - Diagnosis is FREE!
                  </Text>
                </div>
              </div>
            }
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        </>
      )}
    </Modal>
  );
};

export default WaiveDiagnosisModal;
